import { Injectable } from '@angular/core';

export type LogLevel = 'INFO' | 'DEBUG' | 'WARN' | 'ERROR' | 'OTHER';

export interface ILogLinePart {
  type: 'text' | 'link';
  value: string;
  href?: string;
}

export interface IFormattedLogLine {
  raw: string;
  timestamp: string;
  level: LogLevel;
  /** Klasa CSS nadana przez pierwszy pasujący wzorzec (pusta gdy brak dopasowania). */
  lineClass: string;
  /** Treść linii podzielona na fragmenty tekstu i klikalne linki. */
  parts: ILogLinePart[];
  /** URL-e zdjęć wykryte w linii. */
  images: string[];
}

export interface ILogLinePattern {
  id: string;
  match: RegExp;
  lineClass: string;
}

/**
 * Przetwarza linie logów serwera: wykrywa poziom logowania, znacznik czasu,
 * linki (w tym zdjęcia) oraz nadaje liniom klasy CSS na podstawie
 * zdefiniowanych wzorców. Wzorce sprawdzane są po kolei — pierwszy pasujący
 * wygrywa. Nowe formatowanie dodaje się przez dopisanie wzorca do `patterns`
 * i odpowiadającej mu klasy w stylach komponentu wyświetlającego logi.
 */
@Injectable({ providedIn: 'root' })
export class LogLineFormatterService {
  public readonly patterns: ILogLinePattern[] = [
    { id: 'payment', match: /stripe|payment/i, lineClass: 'pattern-payment' },
    { id: 'sms', match: /serwersms|\bsms\b/i, lineClass: 'pattern-sms' },
  ];

  formatContent(content: string): IFormattedLogLine[] {
    if (!content) return [];

    // Merge continuation lines (stack traces, wrapped values) into their parent
    // entry so they inherit its level and styling instead of rendering as bare lines.
    const merged: string[] = [];
    for (const line of content.split('\n')) {
      if (merged.length > 0 && /^(\s|,)/.test(line)) {
        merged[merged.length - 1] += '\n' + line;
      } else {
        merged.push(line);
      }
    }

    return merged.map((raw) => this.formatLine(raw));
  }

  formatLine(raw: string): IFormattedLogLine {
    const level = this.detectLevel(raw);
    const timestamp = this.extractTimestamp(raw);
    const { parts, images } = this.splitParts(raw);
    // Error styling has priority over pattern-based coloring.
    const pattern =
      level === 'ERROR' ? undefined : this.patterns.find((p) => p.match.test(raw));

    return {
      raw,
      timestamp,
      level,
      lineClass: pattern?.lineClass ?? '',
      parts,
      images,
    };
  }

  private detectLevel(raw: string): LogLevel {
    // Exceptions and "Error :" messages are often logged with [INF] severity —
    // content markers take precedence over the level tag.
    if (/Unhandled exception|[A-Za-z.]+Exception\b|Error\s*:/.test(raw)) {
      return 'ERROR';
    }

    // Serilog short codes: [INF] [DBG] [WRN] [ERR] [FTL] + full words
    const bracketMatch = raw.match(/\[(INF|DBG|WRN|ERR|FTL)\]/);
    if (bracketMatch) {
      switch (bracketMatch[1]) {
        case 'INF':
          return 'INFO';
        case 'DBG':
          return 'DEBUG';
        case 'WRN':
          return 'WARN';
        case 'ERR':
        case 'FTL':
          return 'ERROR';
      }
    }

    const wordMatch = raw.match(/\b(INFO|DEBUG|WARN(?:ING)?|ERROR|FATAL)\b/i);
    if (wordMatch) {
      const word = wordMatch[1].toUpperCase();
      if (word.startsWith('WARN')) return 'WARN';
      if (word === 'FATAL') return 'ERROR';
      return word as LogLevel;
    }

    return 'OTHER';
  }

  private extractTimestamp(raw: string): string {
    // "2026-07-19 09:31:46.766 +02:00" or "09:31:46.766"
    const match = raw.match(
      /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}[\.,]\d+(?: [+-]\d{2}:\d{2})?|\d{2}:\d{2}:\d{2}[\.,]\d+)/
    );
    return match ? match[1] : '';
  }

  private splitParts(content: string): {
    parts: ILogLinePart[];
    images: string[];
  } {
    const urlRegex = /https?:\/\/[^\s]+/g;
    const matches: RegExpExecArray[] = [];
    let execMatch: RegExpExecArray | null;
    while ((execMatch = urlRegex.exec(content)) !== null) {
      matches.push(execMatch);
    }
    const images: string[] = [];

    if (!matches.length) {
      return { parts: [{ type: 'text', value: content }], images };
    }

    const parts: ILogLinePart[] = [];
    let currentIndex = 0;

    for (const match of matches) {
      let url = match[0];
      const index = match.index;

      // Trim trailing punctuation that is not part of the URL, e.g. "(image='https://...jpg')."
      const trailingPunctuation = url.match(/['")\].,;:!?]+$/);
      if (trailingPunctuation) {
        url = url.slice(0, -trailingPunctuation[0].length);
      }

      if (index > currentIndex) {
        parts.push({ type: 'text', value: content.slice(currentIndex, index) });
      }

      parts.push({ type: 'link', value: url, href: url });
      if (this.isImageUrl(url)) {
        images.push(url);
      }

      currentIndex = index + url.length;
    }

    if (currentIndex < content.length) {
      parts.push({ type: 'text', value: content.slice(currentIndex) });
    }

    return { parts, images };
  }

  private isImageUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      const imageName = parsedUrl.searchParams.get('imageName') ?? '';
      const candidate = imageName || url;
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(?:$|[?#&])/i.test(candidate);
    } catch {
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(?:$|[?#&])/i.test(url);
    }
  }
}
